import React, { useState, useEffect } from "react";
import axios from "axios";
import "./HostProfile.css";

const HostProfile = ({ hostId }) => {
  const [loading, setLoading] = useState(!!hostId);
  const [error, setError] = useState(null);
  const [hostData, setHostData] = useState(null);

  useEffect(() => {
    const fetchHostDetails = async () => {
      if (!hostId) {
        setError("No host ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        const response = await axios.get(`/user/${hostId}`);

        if (response.data.successStatus) {
          setHostData(response.data.data);
          setError(null);
        } else {
          setError(response.data.message || "Failed to load host data");
        }
      } catch (err) {
        setError(err.response?.data?.error || "Error fetching host data");
      } finally {
        setLoading(false);
      }
    };

    fetchHostDetails();
  }, [hostId]);

  const getInitials = () => {
    if (!hostData) return "";
    return `${hostData.firstName?.[0] || ""}${
      hostData.lastName?.[0] || ""
    }`.toUpperCase();
  };

  const renderProfileImage = () => {
    if (!hostData) return null;
    if (hostData.userPhoto) {
      return (
        <img
          src={hostData?.userPhoto || "./images/avatar.webp"}
          alt="Host Image"
          className="profile-image"
        />
      );
    }

    return (
      <div className="default-profile-image">
        <span className="initials">{getInitials()}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="host-profile-container loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="host-profile-container error-container">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
            fill="#d32f2f"
          />
        </svg>
        <span className="error-text">{error}</span>
      </div>
    );
  }

  if (!hostData) {
    return (
      <div className="host-profile-container error-container">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm0-4h-2V7h2v6z"
            fill="#d32f2f"
          />
        </svg>
        <span className="error-text">Host information not available</span>
      </div>
    );
  }

  return (
    <div className="host-profile-container">
      <div className="profile-content-container">
        <div className="profile-image-container">{renderProfileImage()}</div>
        <div className="info-container">
          <h3 className="name-text">
            {hostData.firstName} {hostData.lastName}
          </h3>

          {hostData.phoneNumber ? (
            <a
              href={`tel:${hostData.phoneNumber}`}
              className="phone-container"
              title={`Call ${hostData.firstName} at ${hostData.phoneNumber}`}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="phone-icon"
              >
                <path
                  d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.24 1.02l-2.2 2.2z"
                  fill="currentColor"
                />
              </svg>
              <span className="phone-text">{hostData.phoneNumber}</span>
            </a>
          ) : (
            <span className="no-phone-text">No phone number available</span>
          )}

          {hostData.email && (
            <span className="email-text">{hostData.email}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default HostProfile;